import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import { superChatSelector } from 'ai/selectors/superChatSelector';
import { coreUserDataSelector } from 'viewModel/selectors/user/coreUserDataSelector';
import SuperChatActions from 'ai/actions/superChatActions';
import FrameworkUtils from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import sdnApi from 'ai/services/sdn';
import { PostLoad } from 'constants/events';
import { getSuperChatConfig, setSuperChatConfig } from 'ai/utils/sessionStorage';

const { wrapHOC, wrapHOCComponent } = FrameworkUtils;
const { getTextFromResource, getLocaleResourceFile, getCurrentLanguage, getCurrentCountry } = LanguageLocaleUtils;
const getText = getLocaleResourceFile('ai/components/SuperChat/locales', 'SuperChat');
const country = getCurrentCountry();
const language = getCurrentLanguage();

const { managerSession, superChatConfig, previousConversations } = sdnApi;

const localization = createStructuredSelector({
    title: getTextFromResource(getText, 'title'),
    placeholder: getTextFromResource(getText, 'placeholder')
});

const functions = (dispatch, _ownProps) => ({
    createSession: async sessionFound => {
        return managerSession(sessionFound).then(data => data);
    },
    closeSuperChat: () => {
        dispatch(SuperChatActions.showSuperChat(false));
    },
    fetchPreviousConversations: clientId => {
        return previousConversations(clientId).then(data => data);
    }
});

const fields = createSelector(superChatSelector, coreUserDataSelector, localization, (superChat, user, locales) => {
    const clientId = user?.biId;

    return {
        localization: locales,
        clientId,
        ...superChat
    };
});

const withSuperChatProps = compose(
    wrapHOC(connect(fields, functions)),
    wrapHOC(WrappedComponent => {
        class SuperChatProps extends React.Component {
            state = {
                config: null
            };

            componentDidMount() {
                Sephora.Util.onLastLoadEvent(window, [PostLoad], () => {
                    const superChatConfigCached = getSuperChatConfig();

                    if (superChatConfigCached) {
                        this.setState({ config: superChatConfigCached });

                        return;
                    }

                    superChatConfig(language, country).then(data => {
                        setSuperChatConfig(data?.data?.config);
                        this.setState({ config: data?.data?.config });
                    });
                });
            }

            render() {
                const { config } = this.state;

                return (
                    <WrappedComponent
                        {...this.props}
                        config={config}
                        language={language}
                        country={country}
                    />
                );
            }
        }

        return wrapHOCComponent(SuperChatProps, 'SuperChatProps', [WrappedComponent]);
    })
);

export {
    withSuperChatProps, fields, functions
};
